import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Star, Percent, Volume, Droplet, BookOpen, Package, ShoppingCart, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { cartAPI } from '../services/api';
import Header from './Header';
import Footer from './Footer';
import RobustImage from './ui/RobustImage';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';

const Collections = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [showAddedDialog, setShowAddedDialog] = useState(false);
  const [addedProductInfo, setAddedProductInfo] = useState(null);
  
  // Get or create session ID for cart
  const getSessionId = () => {
    let sessionId = localStorage.getItem('cartSessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}`;
      localStorage.setItem('cartSessionId', sessionId);
    }
    return sessionId;
  };

  useEffect(() => {
    if (slug) {
      loadCollection(slug);
    } else {
      setSelectedCollection(null); // Reset selected collection when no slug
      loadCollections();
    }
  }, [slug]);

  const loadCollections = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/collections/`);
      
      if (!response.ok) {
        throw new Error('Failed to load collections');
      }

      const data = await response.json();
      if (data.success) {
        setCollections(data.collections || []);
      }
    } catch (error) {
      console.error('Error loading collections:', error);
      setError('Impossibile caricare i campioni');
    } finally {
      setLoading(false);
    }
  };

  const loadCollection = async (collectionSlug) => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/collections/${collectionSlug}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Campione non trovato');
        } else {
          throw new Error('Failed to load collection');
        }
        return;
      }

      const data = await response.json();
      if (data.success) {
        setSelectedCollection(data.collection);
      }
    } catch (error) {
      console.error('Error loading collection:', error);
      setError('Impossibile caricare il campione');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to convert relative image path to full URL
  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // If already a full URL (http/https), return as-is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // If relative path starting with /uploads/, convert to /api/upload/serve/ endpoint
    const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
    
    if (imagePath.startsWith('/uploads/')) {
      // Remove /uploads/ prefix and use /api/upload/serve/ endpoint
      const pathWithoutUploads = imagePath.substring('/uploads/'.length);
      return `${backendUrl}/api/upload/serve/${pathWithoutUploads}`;
    }
    
    // For other relative paths, prepend backend URL
    const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
    return `${backendUrl}/${cleanPath}`;
  };

  const handleSelectProduct = async (collection, product) => {
    try {
      setAddingToCart(true);
      const sessionId = getSessionId();

      // Convert image path to full URL
      const fullImageUrl = getFullImageUrl(product.mainImage);

      // Check if user is on /campioni page or /campioni/:slug
      const isSamplesPage = location.pathname === '/campioni' || location.pathname.startsWith('/campioni');
      
      console.log('🎯 Adding from Collections.js:', {
        pathname: location.pathname,
        isSamplesPage,
        productName: product.name
      });

      // Create cart item for collection product
      const cartItem = {
        id: `collection_${Date.now()}`,
        type: 'collection',
        isSample: isSamplesPage, // Mark as sample if added from /campioni page
        productData: {
          name: product.name,
          liquorType: product.baseProductType,
          liquor: {
            id: product.baseProductType,
            name: product.name,
            basePrice: product.basePrice
          },
          ingredients: product.recipe?.selectedIngredients || [],
          intensity: product.recipe?.aromaticIntensity || 'medium',
          label: { 
            text: product.name, 
            image: fullImageUrl,  // Use full URL for image
            customGraphics: false 
          },
          bottle: {
            id: product.recipe?.bottleType || 'classic',
            name: 'Bottiglia Classica',
            price: 0
          },
          basePrice: product.basePrice,
          quantityDiscounts: [],
          // Collection-specific data
          collectionId: collection.id,
          collectionName: collection.slug,
          collectionTitle: collection.title,
          isSamplesCollection: collection.isSamplesCollection || false, // Propagate samples flag
          productId: product.id,
          isPreConfigured: true,
          collectionProductImage: fullImageUrl  // Store full URL for cart display
        },
        quantity: 1,
        price: product.basePrice
      };

      const result = await cartAPI.addToCart(sessionId, cartItem);
      
      if (result.success) {
        // Considera "campione" se la collezione è marcata come samples OPPURE siamo sotto /campioni
        const isSample = !!collection.isSamplesCollection || location.pathname.startsWith('/campioni');
        if (isSample) {
          // Per i campioni: resta sulla pagina e mostra dialog di scelta
          setAddedProductInfo({
            productName: product.name,
            collectionTitle: collection.title,
            productImage: fullImageUrl
          });
          setShowAddedDialog(true);
        } else {
          toast({
            title: "Prodotto aggiunto al carrello!",
            description: `${product.name} dalla collezione ${collection.title} aggiunto con successo.`,
          });
          navigate('/carrello');
        }
      } else {
        throw new Error(result.message || 'Errore nell\'aggiungere al carrello');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: "Errore",
        description: "Impossibile aggiungere al carrello. Riprova.",
        variant: "destructive"
      });
    } finally {
      setAddingToCart(false);
    }
  };

  // Rimuovi il log di debug (ora sappiamo che funziona)
  const sampleAddedDialog = (
    <Dialog open={showAddedDialog} onOpenChange={setShowAddedDialog}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-sample-added">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-7 h-7 text-green-600" />
            </div>
            <div>
              <DialogTitle className="text-left">Campione aggiunto al carrello</DialogTitle>
              <DialogDescription className="text-left mt-1">
                <strong>{addedProductInfo?.productName}</strong> {addedProductInfo?.collectionTitle ? `— ${addedProductInfo.collectionTitle}` : ''}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="text-sm text-gray-600 py-2">
          Vuoi continuare a scegliere altri campioni o andare direttamente al carrello per completare l&apos;ordine?
        </div>
        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => setShowAddedDialog(false)}
            data-testid="btn-continue-shopping"
            className="w-full sm:flex-1 px-4 py-2.5 rounded-lg border-2 border-purple-600 text-purple-700 font-semibold hover:bg-purple-50 transition-colors"
          >
            Continua con i campioni
          </button>
          <button
            type="button"
            onClick={() => { setShowAddedDialog(false); navigate('/carrello'); }}
            data-testid="btn-go-to-cart"
            className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-semibold hover:from-purple-700 hover:to-fuchsia-700 shadow"
          >
            <ShoppingCart className="w-4 h-4" />
            Vai al carrello
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Caricamento campioni...</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center min-h-[400px] flex items-center justify-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Oops! Qualcosa è andato storto</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <Link 
                to="/"
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Torna alla Homepage
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show specific collection
  if (selectedCollection) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-purple-600">Home</Link>
            <span>›</span>
            <Link to="/campioni" className="hover:text-purple-600">Campioni</Link>
            <span>›</span>
            <span className="text-gray-900">{selectedCollection.title}</span>
          </div>

          {/* Collection Header */}
          <div className="bg-white rounded-lg p-8 mb-8">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">{selectedCollection.title}</h1>
              {selectedCollection.subtitle && (
                <p className="text-xl text-gray-600 mb-6">{selectedCollection.subtitle}</p>
              )}
              <p className="text-gray-700 leading-relaxed">{selectedCollection.description}</p>
            </div>
          </div>

          {/* Products Grid */}
          {selectedCollection.products && selectedCollection.products.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {selectedCollection.products
                .filter(product => product.isActive)
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((product) => (
                <div key={product.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                  {/* Product Image - Always Square */}
                  <div className="relative aspect-square bg-gray-100">
                    <RobustImage
                      src={getFullImageUrl(product.mainImage)}
                      category="products"
                      itemId={product.baseProductType}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                    {product.isFeatured && (
                      <div className="absolute top-3 right-3">
                        <Star className="w-5 h-5 text-yellow-500 fill-current" />
                      </div>
                    )}
                    <div className="absolute top-3 left-3">
                      <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                        {product.category}
                      </span>
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-xl font-bold text-gray-900">{product.name}</h3>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-purple-600">€{product.basePrice}</div>
                        <div className="text-sm text-gray-500">IVA incl.</div>
                      </div>
                    </div>

                    <p className="text-gray-600 mb-4 leading-relaxed">{product.shortDescription}</p>

                    {/* Product Details */}
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-1">
                          <Percent className="w-4 h-4" />
                          <span>{product.alcoholPercentage}°</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Volume className="w-4 h-4" />
                          <span>{product.volume}ML</span>
                        </div>
                      </div>
                      <div className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {product.baseProductType.toUpperCase()}
                      </div>
                    </div>

                    {/* Fixed Recipe Badge */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center space-x-2">
                        <Droplet className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">Ricetta Preimpostata</span>
                      </div>
                      <p className="text-xs text-green-600 mt-1">
                        Ricetta già bilanciata dai nostri maestri distillatori
                      </p>
                    </div>

                    {/* Action Button */}
                    <button
                      onClick={() => handleSelectProduct(selectedCollection, product)}
                      disabled={addingToCart}
                      className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                        addingToCart
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-purple-600 text-white hover:bg-purple-700'
                      }`}
                    >
                      {addingToCart ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Aggiungendo al carrello...
                        </div>
                      ) : (
                        'Aggiungi al Carrello'
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg p-12 text-center">
              <div className="max-w-md mx-auto">
                <div className="text-gray-400 mb-4">
                  <Package className="w-16 h-16 mx-auto" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">Nessun prodotto disponibile</h3>
                <p className="text-gray-600">
                  Questa collezione è in fase di preparazione. Torna presto per scoprire i nuovi prodotti!
                </p>
              </div>
            </div>
          )}
        </div>

        <Footer />
        {sampleAddedDialog}
      </div>
    );
  }

  // Show all collections
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-purple-600">Home</Link>
          <span>›</span>
          <span className="text-gray-900">Campioni</span>
        </div>

        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">I Nostri Campioni</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Scopri i nostri campioni esclusivi di liquori artigianali con ricette preimpostate dai nostri maestri distillatori.
          </p>
        </div>

        {/* Collections Grid */}
        {collections.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {collections
              .filter(collection => collection.isActive)
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((collection) => (
              <Link
                key={collection.id}
                to={`/campioni/${collection.slug}`}
                className="group bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
              >
                {/* Collection Banner - Always Square */}
                <div className="relative aspect-square bg-gradient-to-br from-purple-600 to-purple-800">
                  {collection.bannerImage && (
                    <RobustImage
                      src={collection.bannerImage}
                      category="collections"
                      itemId={collection.slug}
                      alt={collection.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-10 transition-all" />
                  <div className="absolute bottom-4 left-4 text-white">
                    <h2 className="text-2xl font-bold mb-1">{collection.title}</h2>
                    {collection.subtitle && (
                      <p className="text-sm opacity-90">{collection.subtitle}</p>
                    )}
                  </div>
                  {collection.isFeatured && (
                    <div className="absolute top-4 right-4">
                      <Star className="w-6 h-6 text-yellow-400 fill-current" />
                    </div>
                  )}
                </div>

                {/* Collection Info */}
                <div className="p-6">
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {collection.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      {collection.products?.length || 0} prodotti disponibili
                    </div>
                    <div className="text-purple-600 font-medium text-sm group-hover:text-purple-700">
                      Esplora →
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="text-gray-400 mb-4">
                <BookOpen className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Campioni in Arrivo</h3>
              <p className="text-gray-600">
                Stiamo preparando dei campioni esclusivi per te. Torna presto per scoprirli!
              </p>
            </div>
          </div>
        )}
      </div>

      {sampleAddedDialog}

      <Footer />
    </div>
  );
};

export default Collections;